import { HttpClient, HttpParams, HttpHeaders, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import * as i0 from '@angular/core';
import { inject, Injectable, NgModule } from '@angular/core';
import { first, map, switchMap } from 'rxjs/operators';
import { CodedError } from '@tc/foundation/utilities';
import { EnvironmentService } from '@tc/foundation';
import 'reflect-metadata';

var BaseApiErrorCodes;
(function (BaseApiErrorCodes) {
    BaseApiErrorCodes["NoHttpMethodOnContract"] = "NoHttpMethodOnContract";
    // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
    BaseApiErrorCodes["WrongHttpMethodOnContract"] = "NoHttpMethodOnContract";
    BaseApiErrorCodes["NoEndpointOnContract"] = "NoEndpointOnContract";
    BaseApiErrorCodes["ApiContractMissing"] = "ApiContractMissing";
    BaseApiErrorCodes["ContractNotConcrete"] = "ContractNotConcrete";
    BaseApiErrorCodes["RouteParameterMismatch"] = "RouteParameterMismatch";
})(BaseApiErrorCodes || (BaseApiErrorCodes = {}));

var HttpMethods;
(function (HttpMethods) {
    HttpMethods["get"] = "GET";
    HttpMethods["post"] = "POST";
    HttpMethods["put"] = "PUT";
    HttpMethods["patch"] = "PATCH";
    HttpMethods["delete"] = "DELETE";
})(HttpMethods || (HttpMethods = {}));

function getConstructorName$1(obj) {
    return obj.constructor.name;
}
class UrlBuilderService {
    constructor() {
        this._envService = inject(EnvironmentService);
    }
    buildUrl(endpointOrFullUrl, routeValue) {
        //if the url contains a parameter placeholder we need to remove the placeholder name from it so that the calling method can append its actual value
        endpointOrFullUrl = routeValue
            ? endpointOrFullUrl.replace(/\{\w+\}/, routeValue)
            : endpointOrFullUrl;
        const isAbsolute = endpointOrFullUrl.startsWith('http') ||
            endpointOrFullUrl.startsWith('ftp');
        const prependedSlash = endpointOrFullUrl.startsWith('/');
        return this._envService.configuration$.pipe(first(), map((configuration) => isAbsolute
            ? `${endpointOrFullUrl}`
            : `${configuration.apiUrl}${prependedSlash ? '' : '/'}${endpointOrFullUrl}`));
    }
    getRouteValue(contract, endpoint) {
        const match = new RegExp(/\{(\w+)\}/).exec(endpoint);
        const routeParam = match ? match[1] : undefined;
        if (routeParam) {
            const routeParamIndex = routeParam;
            const routeValueKey = contract[routeParamIndex];
            if (!routeValueKey) {
                throw new CodedError(BaseApiErrorCodes.RouteParameterMismatch, `The parameter ${routeParam} on the route does not match any property on the contract.`);
            }
            return routeValueKey.toString();
        }
        return undefined;
    }
    validateContract(contract) {
        if (getConstructorName$1(contract) === Object.name) {
            throw new CodedError(BaseApiErrorCodes.ContractNotConcrete, 'Provided contract is not a concrete type. (Possibly casted?)');
        }
        const constructor = contract.constructor;
        if (!Reflect.hasMetadata('apiContract', constructor)) {
            throw new CodedError(BaseApiErrorCodes.ApiContractMissing, `${getConstructorName$1(contract)} must include an apiContract`);
        }
        const metaData = Reflect.getMetadata('apiContract', constructor);
        if (!metaData.endpoint) {
            throw new CodedError(BaseApiErrorCodes.NoEndpointOnContract, `${getConstructorName$1(contract)} does not have an apiContract endpoint defined`);
        }
        if (!metaData.httpMethod) {
            throw new CodedError(BaseApiErrorCodes.NoHttpMethodOnContract, `${getConstructorName$1(contract)} does not have an apiContract httpMethod defined`);
        }
        return metaData;
    }
    static { this.ɵfac = function UrlBuilderService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || UrlBuilderService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: UrlBuilderService, factory: UrlBuilderService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UrlBuilderService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], null, null); })();

function getConstructorName(obj) {
    return obj.constructor.name;
}
class HttpService {
    constructor() {
        this._http = inject(HttpClient);
        this._urlBuilder = inject(UrlBuilderService);
    }
    send(contract, httpParams, headers) {
        const metaData = this._urlBuilder.validateContract(contract);
        const routeValue = this._urlBuilder.getRouteValue(contract, metaData.endpoint);
        if ((metaData.httpMethod === HttpMethods.get ||
            metaData.httpMethod === HttpMethods.delete) &&
            !!httpParams) {
            throw new CodedError('HttpParamsWithGet', 'Explicit HttpParams should not defined with get or delete http methods');
        }
        const httpOptions = metaData.httpMethod === HttpMethods.get ||
            metaData.httpMethod === HttpMethods.delete
            ? {
                params: new HttpParams({
                    fromObject: contract,
                }),
                headers,
            }
            : {
                body: contract,
                params: httpParams,
                headers,
            };
        return this._urlBuilder
            .buildUrl(metaData.endpoint, routeValue)
            .pipe(switchMap((url) => this._http.request(metaData.httpMethod, url, httpOptions)));
    }
    download(contract, options) {
        const metaData = this._urlBuilder.validateContract(contract);
        const routeValue = this._urlBuilder.getRouteValue(contract, metaData.endpoint);
        if (metaData.httpMethod === HttpMethods.put ||
            metaData.httpMethod === HttpMethods.patch ||
            metaData.httpMethod === HttpMethods.delete) {
            throw new CodedError('HttpMethodInvalid', 'A download request must be HttpMethods.get or HttpMethods.post');
        }
        if (metaData.httpMethod === HttpMethods.get && !!options?.httpParams) {
            throw new CodedError('HttpParamsWithGet', 'Explicit HttpParams should not defined with HttpMethods.get');
        }
        const headers = options?.headers
            ? options.headers.set('Content-Type', 'text/plain; charset=utf-8')
            : new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');
        const httpOptions = metaData.httpMethod === HttpMethods.get
            ? {
                params: new HttpParams({
                    fromObject: contract,
                }),
                headers,
                responseType: 'text',
            }
            : {
                body: contract,
                params: options?.httpParams,
                headers,
                responseType: 'text',
            };
        return this._urlBuilder
            .buildUrl(metaData.endpoint, routeValue)
            .pipe(switchMap((url) => this._http.request(metaData.httpMethod, url, httpOptions)));
    }
    delete(contract, headers) {
        const metaData = this._urlBuilder.validateContract(contract);
        if (metaData.httpMethod !== HttpMethods.delete) {
            throw HttpMethodError(getConstructorName(contract), HttpMethods.delete, metaData.httpMethod);
        }
        const routeValue = this._urlBuilder.getRouteValue(contract, metaData.endpoint);
        return this._urlBuilder.buildUrl(metaData.endpoint, routeValue).pipe(switchMap((url) => this._http.delete(url, {
            params: new HttpParams({
                fromObject: contract,
            }),
            headers,
        })));
    }
    get(contract, headers) {
        const metaData = this._urlBuilder.validateContract(contract);
        if (metaData.httpMethod !== HttpMethods.get) {
            throw HttpMethodError(getConstructorName(contract), HttpMethods.get, metaData.httpMethod);
        }
        const routeValue = this._urlBuilder.getRouteValue(contract, metaData.endpoint);
        return this._urlBuilder.buildUrl(metaData.endpoint, routeValue).pipe(switchMap((url) => this._http.get(url, {
            params: new HttpParams({
                fromObject: contract,
            }),
            headers,
        })));
    }
    post(contract, httpParams, headers) {
        const metaData = this._urlBuilder.validateContract(contract);
        if (metaData.httpMethod !== HttpMethods.post) {
            throw HttpMethodError(getConstructorName(contract), HttpMethods.post, metaData.httpMethod);
        }
        const routeValue = this._urlBuilder.getRouteValue(contract, metaData.endpoint);
        return this._urlBuilder.buildUrl(metaData.endpoint, routeValue).pipe(switchMap((url) => this._http.post(url, contract, {
            headers,
            params: httpParams,
        })));
    }
    put(contract, httpParams, headers) {
        const metaData = this._urlBuilder.validateContract(contract);
        if (metaData.httpMethod !== HttpMethods.put) {
            throw HttpMethodError(getConstructorName(contract), HttpMethods.put, metaData.httpMethod);
        }
        const routeValue = this._urlBuilder.getRouteValue(contract, metaData.endpoint);
        return this._urlBuilder.buildUrl(metaData.endpoint, routeValue).pipe(switchMap((url) => this._http.put(url, contract, {
            headers,
            params: httpParams,
        })));
    }
    patch(contract, httpParams, headers) {
        const metaData = this._urlBuilder.validateContract(contract);
        if (metaData.httpMethod !== HttpMethods.patch) {
            throw HttpMethodError(getConstructorName(contract), HttpMethods.patch, metaData.httpMethod);
        }
        const routeValue = this._urlBuilder.getRouteValue(contract, metaData.endpoint);
        return this._urlBuilder.buildUrl(metaData.endpoint, routeValue).pipe(switchMap((url) => this._http.patch(url, contract, {
            headers,
            params: httpParams,
        })));
    }
    static { this.ɵfac = function HttpService_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || HttpService)(); }; }
    static { this.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: HttpService, factory: HttpService.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(HttpService, [{
        type: Injectable,
        args: [{
                providedIn: 'root',
            }]
    }], null, null); })();
const HttpMethodError = (name, expectedMethod, actualMethod) => new CodedError(BaseApiErrorCodes.WrongHttpMethodOnContract, `${name} is not ${expectedMethod}. Method on contract: ${actualMethod}`);

/**
 * The HttpService is already provided at root, as specified in its injectable decorator,
 * so importing this module is not necessary. If it is imported, then it is not necessary
 * to import HttpClientModule in the implementing project, as it is imported and exported
 * here.
 */
class HttpModule {
    static { this.ɵfac = function HttpModule_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || HttpModule)(); }; }
    static { this.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: HttpModule }); }
    static { this.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ providers: [provideHttpClient(withInterceptorsFromDi())] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(HttpModule, [{
        type: NgModule,
        args: [{
                declarations: [],
                exports: [],
                imports: [],
                providers: [provideHttpClient(withInterceptorsFromDi())],
            }]
    }], null, null); })();

/* eslint-disable */
/** Standard HTTP status codes */
var HttpStatusCode;
(function (HttpStatusCode) {
    // ================== BEGIN STANDARD CODES ========================
    /** The server has received the request headers and the client should proceed to send the request
     * body (in the case of a request for which a body needs to be sent; for example, a POST request).
     * Sending a large request body to a server after a request has been rejected for inappropriate headers
     * would be inefficient. To have a server check the request's headers, a client must send Expect: 100-continue
     * as a header in its initial request and receive a 100 Continue status code in response before sending the body.
     * If the client receives an error code such as 403 (Forbidden) or 405 (Method Not Allowed) then it shouldn't send
     * the request's body. The response 417 Expectation Failed indicates that the request should be repeated without
     * the Expect header as it indicates that the server doesn't support expectations (this is the case, for example, of HTTP/1.0 servers). */
    HttpStatusCode[HttpStatusCode["CONTINUE"] = 100] = "CONTINUE";
    /** The requester has asked the server to switch protocols and the server has agreed to do so. */
    HttpStatusCode[HttpStatusCode["SWITCHING_PROTOCOLS"] = 101] = "SWITCHING_PROTOCOLS";
    /** A WebDAV request may contain many sub-requests involving file operations, requiring a long time
     * to complete the request. This code indicates that the server has received and is processing the request,
     * but no response is available yet. This prevents the client from timing out and assuming the request was lost. */
    HttpStatusCode[HttpStatusCode["PROCESSING"] = 102] = "PROCESSING";
    /** Used to return some response headers before file HTTP message. */
    HttpStatusCode[HttpStatusCode["EARLY_HINTS"] = 103] = "EARLY_HINTS";
    /** Standard response for successful HTTP requests. The actual response will depend on the request method used.
     * In a GET request, the response will contain an entity corresponding to the requested resource. In a POST request,
     * the response will contain an entity describing or containing the result of the action. */
    HttpStatusCode[HttpStatusCode["OK"] = 200] = "OK";
    /** The request has been fulfilled, resulting in the creation of a new resource. */
    HttpStatusCode[HttpStatusCode["CREATED"] = 201] = "CREATED";
    /** The request has been accepted for processing, but the processing has not been completed. The request might or might
     * not be eventually acted upon, and may be disallowed when processing occurs. */
    HttpStatusCode[HttpStatusCode["ACCEPTED"] = 202] = "ACCEPTED";
    /** The server is a transforming proxy (e.g. a Web accelerator) that received a 200 OK from its origin, but is returning
     * a modified version of the origin's response. */
    HttpStatusCode[HttpStatusCode["NON_AUTHORITATIVE_INFORMATION"] = 203] = "NON_AUTHORITATIVE_INFORMATION";
    /** The server successfully processed the request and is not returning any content. */
    HttpStatusCode[HttpStatusCode["NO_CONTENT"] = 204] = "NO_CONTENT";
    /** The server successfully processed the request, but is not returning any content. Unlike a 204 response, this response
     * requires that the requester reset the document view. */
    HttpStatusCode[HttpStatusCode["RESET_CONTENT"] = 205] = "RESET_CONTENT";
    /** The server is delivering only part of the resource (byte serving) due to a range header sent by the client. The range
     * header is used by HTTP clients to enable resuming of interrupted downloads, or split a download into multiple simultaneous streams. */
    HttpStatusCode[HttpStatusCode["PARTIAL_CONTENT"] = 206] = "PARTIAL_CONTENT";
    /** The message body that follows is by default an XML message and can contain a number of separate response codes, depending
     * on how many sub-requests were made. */
    HttpStatusCode[HttpStatusCode["MULTI_STATUS"] = 207] = "MULTI_STATUS";
    /** The members of a DAV binding have already been enumerated in a preceding part of the (multistatus) response, and are not
     * being included again. */
    HttpStatusCode[HttpStatusCode["ALREADY_REPORTED"] = 208] = "ALREADY_REPORTED";
    /** The server has fulfilled a request for the resource, and the response is a representation of the result of one or more
     * instance-manipulations applied to the current instance */
    HttpStatusCode[HttpStatusCode["IM_USED"] = 209] = "IM_USED";
    /** Indicates multiple options for the resource from which the client may choose (via agent-driven content negotiation).
     * For example, this code could be used to present multiple video format options, to list files with different filename
     * extensions, or to suggest word-sense disambiguation. */
    HttpStatusCode[HttpStatusCode["MULTIPLE_CHOICES"] = 300] = "MULTIPLE_CHOICES";
    /** This and all future requests should be directed to the given URI. */
    HttpStatusCode[HttpStatusCode["MOVED_PERMANENTLY"] = 301] = "MOVED_PERMANENTLY";
    /** This is an example of industry practice contradicting the standard. The HTTP/1.0 specification (RFC 1945) required the
     * client to perform a temporary redirect (the original describing phrase was "Moved Temporarily"), but popular browsers implemented
     * 302 with the functionality of a 303 See Other. Therefore, HTTP/1.1 added status codes 303 and 307 to distinguish between the two
     * behaviours. However, some Web applications and frameworks use the 302 status code as if it were the 303. */
    HttpStatusCode[HttpStatusCode["FOUND"] = 302] = "FOUND";
    /** The response to the request can be found under another URI using the GET method. When received in response to a POST (or PUT/DELETE),
     * the client should presume that the server has received the data and should issue a new GET request to the given URI. */
    HttpStatusCode[HttpStatusCode["SEE_OTHER"] = 303] = "SEE_OTHER";
    /** Indicates that the resource has not been modified since the version specified by the request headers If-Modified-Since or
     * If-None-Match. In such case, there is no need to retransmit the resource since the client still has a previously-downloaded copy. */
    HttpStatusCode[HttpStatusCode["NOT_MODIFIED"] = 304] = "NOT_MODIFIED";
    /** The requested resource is available only through a proxy, the address for which is provided in the response. Many HTTP clients
     * (such as Mozilla and Internet Explorer) do not correctly handle responses with this status code, primarily for security reasons. */
    HttpStatusCode[HttpStatusCode["USE_PROXY"] = 305] = "USE_PROXY";
    /** No longer used. Originally meant "Subsequent requests should use the specified proxy.*/
    HttpStatusCode[HttpStatusCode["SWITCH_PROXY"] = 306] = "SWITCH_PROXY";
    /** In this case, the request should be repeated with another URI; however, future requests should still use the original URI.
     * In contrast to how 302 was historically implemented, the request method is not allowed to be changed when reissuing the original
     * request. For example, a POST request should be repeated using another POST request. */
    HttpStatusCode[HttpStatusCode["TEMPORARY_REDIRECT"] = 307] = "TEMPORARY_REDIRECT";
    /** The request and all future requests should be repeated using another URI. 307 and 308 parallel the behaviors of 302 and 301, but
     * do not allow the HTTP method to change. So, for example, submitting a form to a permanently redirected resource may continue
     * smoothly */
    HttpStatusCode[HttpStatusCode["PERMANENT_REDIRECT"] = 308] = "PERMANENT_REDIRECT";
    /** The server cannot or will not process the request due to an apparent client error (e.g., malformed request syntax, size too
     * large, invalid request message framing, or deceptive request routing). */
    HttpStatusCode[HttpStatusCode["BAD_REQUEST"] = 400] = "BAD_REQUEST";
    /** Similar to 403 Forbidden, but specifically for use when authentication is required and has failed or has not yet been provided.
   * The response must include a WWW-Authenticate header field containing a challenge applicable to the requested resource. See Basic
   * access authentication and Digest access authentication. 401 semantically means "unauthenticated", i.e. the user does not have the
   * necessary credentials.
   Note: Some sites issue HTTP 401 when an IP address is banned from the website (usually the website domain) and that specific address
   is refused permission to access a website. */
    HttpStatusCode[HttpStatusCode["UNAUTHORIZED"] = 401] = "UNAUTHORIZED";
    /** Reserved for future use. The original intention was that this code might be used as part of some form of digital cash or micropayment
     * scheme, as proposed for example by GNU Taler, but that has not yet happened, and this code is not usually used. Google Developers API
     * uses this status if a particular developer has exceeded the daily limit on requests. Stripe API uses this code for errors with
     * processing credit cards. */
    HttpStatusCode[HttpStatusCode["PAYMENT_REQUIRED"] = 402] = "PAYMENT_REQUIRED";
    /** The request was valid, but the server is refusing action. The user might not have the necessary permissions for a resource,
     * or may need an account of some sort. */
    HttpStatusCode[HttpStatusCode["FORBIDDEN"] = 403] = "FORBIDDEN";
    /** The requested resource could not be found but may be available in the future. Subsequent requests by the client are permissible. */
    HttpStatusCode[HttpStatusCode["NOT_FOUND"] = 404] = "NOT_FOUND";
    /** A request method is not supported for the requested resource; for example, a GET request on a form that requires data to be
     * presented via POST, or a PUT request on a read-only resource. */
    HttpStatusCode[HttpStatusCode["METHOD_NOT_ALLOWED"] = 405] = "METHOD_NOT_ALLOWED";
    /** The requested resource is capable of generating only content not acceptable according to the Accept headers sent in the request. */
    HttpStatusCode[HttpStatusCode["NOT_ACCEPTABLE"] = 406] = "NOT_ACCEPTABLE";
    /** The client must first authenticate itself with the proxy. */
    HttpStatusCode[HttpStatusCode["PROXY_AUTHENTICATION_REQUIRED"] = 407] = "PROXY_AUTHENTICATION_REQUIRED";
    /** The server timed out waiting for the request. According to HTTP specifications: "The client did not produce a request within
     * the time that the server was prepared to wait. The client MAY repeat the request without modifications at any later time." */
    HttpStatusCode[HttpStatusCode["REQUEST_TIMEOUT"] = 408] = "REQUEST_TIMEOUT";
    /** Indicates that the request could not be processed because of conflict in the request, such as an edit conflict between multiple
     * simultaneous updates. */
    HttpStatusCode[HttpStatusCode["CONFLICT"] = 409] = "CONFLICT";
    /** Indicates that the resource requested is no longer available and will not be available again. This should be used when a
     * resource has been intentionally removed and the resource should be purged. Upon receiving a 410 status code, the client should
     * not request the resource in the future. Clients such as search engines should remove the resource from their indices. Most use
     * cases do not require clients and search engines to purge the resource, and a "404 Not Found" may be used instead. */
    HttpStatusCode[HttpStatusCode["GONE"] = 410] = "GONE";
    /** The request did not specify the length of its content, which is required by the requested resource. */
    HttpStatusCode[HttpStatusCode["LENGTH_REQUIRED"] = 411] = "LENGTH_REQUIRED";
    /** The server does not meet one of the preconditions that the requester put on the request. */
    HttpStatusCode[HttpStatusCode["PRECONDITION_FAILED"] = 412] = "PRECONDITION_FAILED";
    /** The request is larger than the server is willing or able to process. Previously called "Request Entity Too Large". */
    HttpStatusCode[HttpStatusCode["PAYLOAD_TOO_LARGE"] = 413] = "PAYLOAD_TOO_LARGE";
    /** The URI provided was too long for the server to process. Often the result of too much data being encoded as a query-string of
     * a GET request, in which case it should be converted to a POST request. Called "Request-URI Too Long" previously. */
    HttpStatusCode[HttpStatusCode["URI_TOO_LONG"] = 414] = "URI_TOO_LONG";
    /** The request entity has a media type which the server or resource does not support. For example, the client uploads an image as
     * image/svg+xml, but the server requires that images use a different format. */
    HttpStatusCode[HttpStatusCode["UNSUPPORTED_MEDIA_TYPE"] = 415] = "UNSUPPORTED_MEDIA_TYPE";
    /** The client has asked for a portion of the file (byte serving), but the server cannot supply that portion. For example, if the
     * client asked for a part of the file that lies beyond the end of the file. Called "Requested Range Not Satisfiable" previously. */
    HttpStatusCode[HttpStatusCode["RANGE_NOT_SATISFIABLE"] = 416] = "RANGE_NOT_SATISFIABLE";
    /** The server cannot meet the requirements of the Expect request-header field. */
    HttpStatusCode[HttpStatusCode["EXPECTATION_FAILED"] = 417] = "EXPECTATION_FAILED";
    /** This code was defined in 1998 as one of the traditional IETF April Fools' jokes, in RFC 2324, Hyper Text Coffee Pot Control
     * Protocol, and is not expected to be implemented by actual HTTP servers. The RFC specifies this code should be returned by
     * teapots requested to brew coffee. This HTTP status is used as an Easter egg in some websites, including Google.com. */
    HttpStatusCode[HttpStatusCode["IM_A_TEAPOT"] = 418] = "IM_A_TEAPOT";
    /** The request was directed at a server that is not able to produce a response. (for example because of a connection reuse) */
    HttpStatusCode[HttpStatusCode["MISDIRECT_REQUEST"] = 421] = "MISDIRECT_REQUEST";
    /** The request was well-formed but was unable to be followed due to semantic errors. */
    HttpStatusCode[HttpStatusCode["UNPROCESSABLE_ENTITY"] = 422] = "UNPROCESSABLE_ENTITY";
    /** The resource that is being accessed is locked. */
    HttpStatusCode[HttpStatusCode["LOCKED"] = 423] = "LOCKED";
    /** The request failed because it depended on another request and that request failed (e.g., a PROPPATCH). */
    HttpStatusCode[HttpStatusCode["FAILED_DEPENDENCY"] = 424] = "FAILED_DEPENDENCY";
    /** The client should switch to a different protocol such as TLS/1.0, given in the Upgrade header field. */
    HttpStatusCode[HttpStatusCode["UPGRADE_REQUIRED"] = 426] = "UPGRADE_REQUIRED";
    /** The origin server requires the request to be conditional. Intended to prevent the 'lost update' problem, where a client
     * GETs a resource's state, modifies it, and PUTs it back to the server, when meanwhile a third party has modified the state
     * on the server, leading to a conflict." */
    HttpStatusCode[HttpStatusCode["PRECONDITION_REQUIRED"] = 428] = "PRECONDITION_REQUIRED";
    /** The user has sent too many requests in a given amount of time. Intended for use with rate-limiting schemes. */
    HttpStatusCode[HttpStatusCode["TOO_MANY_REQUESTS"] = 429] = "TOO_MANY_REQUESTS";
    /** The server is unwilling to process the request because either an individual header field, or all the header fields
     * collectively, are too large. */
    HttpStatusCode[HttpStatusCode["REQUEST_HEADER_FIELDS_TOO_LARGE"] = 431] = "REQUEST_HEADER_FIELDS_TOO_LARGE";
    /** A server operator has received a legal demand to deny access to a resource or to a set of resources that includes the
     * requested resource. The code 451 was chosen as a reference to the novel Fahrenheit 451 (see the Acknowledgements in the RFC). */
    HttpStatusCode[HttpStatusCode["UNAVAILABLE_FOR_LEGAL_REASONS"] = 451] = "UNAVAILABLE_FOR_LEGAL_REASONS";
    /** A generic error message, given when an unexpected condition was encountered and no more specific message is suitable. */
    HttpStatusCode[HttpStatusCode["INTERNAL_SERVER_ERROR"] = 500] = "INTERNAL_SERVER_ERROR";
    /** The server either does not recognize the request method, or it lacks the ability to fulfil the request. Usually this
     * implies future availability (e.g., a new feature of a web-service API). */
    HttpStatusCode[HttpStatusCode["NOT_IMPLEMENTED"] = 501] = "NOT_IMPLEMENTED";
    /** The server was acting as a gateway or proxy and received an invalid response from the upstream server. */
    HttpStatusCode[HttpStatusCode["BAD_GATEWAY"] = 502] = "BAD_GATEWAY";
    /** The server is currently unavailable (because it is overloaded or down for maintenance). Generally, this is a temporary state. */
    HttpStatusCode[HttpStatusCode["SERVICE_UNAVAILABLE"] = 503] = "SERVICE_UNAVAILABLE";
    /** The server was acting as a gateway or proxy and did not receive a timely response from the upstream server. */
    HttpStatusCode[HttpStatusCode["GATEWAY_TIMEOUT"] = 504] = "GATEWAY_TIMEOUT";
    /** The server does not support the HTTP protocol version used in the request. */
    HttpStatusCode[HttpStatusCode["HTTP_VERSION_NOT_SUPPORTED"] = 505] = "HTTP_VERSION_NOT_SUPPORTED";
    /** Transparent content negotiation for the request results in a circular reference. */
    HttpStatusCode[HttpStatusCode["VARIANT_ALSO_NEGOTIATES"] = 506] = "VARIANT_ALSO_NEGOTIATES";
    /** The server is unable to store the representation needed to complete the request. */
    HttpStatusCode[HttpStatusCode["INSUFFICIENT_STORAGE"] = 507] = "INSUFFICIENT_STORAGE";
    /** The server detected an infinite loop while processing the request (sent in lieu of 208 Already Reported). */
    HttpStatusCode[HttpStatusCode["LOOP_DETECTED"] = 508] = "LOOP_DETECTED";
    /** Further extensions to the request are required for the server to fulfil it. */
    HttpStatusCode[HttpStatusCode["NOT_EXTENDED"] = 510] = "NOT_EXTENDED";
    /** The client needs to authenticate to gain network access. Intended for use by intercepting proxies used to control access to
     * the network (e.g., "captive portals" used to require agreement to Terms of Service before granting full Internet access via
     * a Wi-Fi hotspot). */
    HttpStatusCode[HttpStatusCode["NETWORK_AUTHENTICATION_REQUIRED"] = 511] = "NETWORK_AUTHENTICATION_REQUIRED";
    // ================== BEGIN NON-STANDARD CODES ========================
    /** Used in the resumable requests proposal to resume aborted PUT or POST requests.[69] */
    HttpStatusCode[HttpStatusCode["CHECKPOINT"] = 103] = "CHECKPOINT";
    /** A deprecated response used by the Spring Framework when a method has failed.[70] */
    HttpStatusCode[HttpStatusCode["METHOD_FAILURE_SPRING_FRAMEWORK"] = 420] = "METHOD_FAILURE_SPRING_FRAMEWORK";
    /** Returned by version 1 of the Twitter Search and Trends API when the client is being rate limited; versions 1.1 and later use
     * the 429 Too Many Requests response code instead.[71] */
    HttpStatusCode[HttpStatusCode["ENHANCE_YOUR_CALM"] = 420] = "ENHANCE_YOUR_CALM";
    /** The Microsoft extension code indicated when Windows Parental Controls are turned on and are blocking access to the requested
     * webpage.[72] */
    HttpStatusCode[HttpStatusCode["BLOCKED_BY_WINDOWS_PARENTAL_CONTROLS"] = 450] = "BLOCKED_BY_WINDOWS_PARENTAL_CONTROLS";
    /** Returned by ArcGIS for Server. Code 498 indicates an expired or otherwise invalid token.[73] */
    HttpStatusCode[HttpStatusCode["INVALID_TOKEN_ESRI"] = 498] = "INVALID_TOKEN_ESRI";
    /** Returned by ArcGIS for Server. Code 499 indicates that a token is required but was not submitted.[73] */
    HttpStatusCode[HttpStatusCode["TOKEN_REQUIRED_ESRI"] = 499] = "TOKEN_REQUIRED_ESRI";
    /** Used by GoCardless to indicate that a given token or secret is invalid */
    HttpStatusCode[HttpStatusCode["INVALID_GO_CARDLESS_TOKEN"] = 498] = "INVALID_GO_CARDLESS_TOKEN";
    /** The server has exceeded the bandwidth specified by the server administrator; this is often used by shared hosting providers
     * to limit the bandwidth of customers.[74] */
    HttpStatusCode[HttpStatusCode["BANDWIDTH_LIMIT_EXCEEDED"] = 509] = "BANDWIDTH_LIMIT_EXCEEDED";
    /** Used by the Pantheon web platform to indicate a site that has been frozen due to inactivity.[75] */
    HttpStatusCode[HttpStatusCode["SITE_IS_FROZEN"] = 530] = "SITE_IS_FROZEN";
    /** Used by some HTTP proxies to signal a network read timeout behind the proxy to a client in front of the proxy.[76] */
    HttpStatusCode[HttpStatusCode["NETWORK_READ_TIMEOUT_ERROR"] = 598] = "NETWORK_READ_TIMEOUT_ERROR";
    // =================== BEGIN MICROSOFT IIS =========================
    /** The client's session has expired and must log in again.[77] */
    HttpStatusCode[HttpStatusCode["IIS_LOGIN_TIMEOUT"] = 440] = "IIS_LOGIN_TIMEOUT";
    /** The server cannot honour the request because the user has not provided the required information.[78] */
    HttpStatusCode[HttpStatusCode["IIS_RETRY_WITH"] = 449] = "IIS_RETRY_WITH";
    /** Used in Exchange ActiveSync when either a more efficient server is available or the server cannot access the users'
     * mailbox.[79] The client is expected to re-run the HTTP AutoDiscover operation to find a more appropriate server.[80] */
    HttpStatusCode[HttpStatusCode["IIS_REDIRECT"] = 451] = "IIS_REDIRECT";
    // ==================== BEGIN NGINX =================================
    /** Used to indicate that the server has returned no information to the client and closed the connection. */
    HttpStatusCode[HttpStatusCode["NGINX_NO_RESPONSE"] = 444] = "NGINX_NO_RESPONSE";
    /** An expansion of the 400 Bad Request response code, used when the client has provided an invalid client certificate. */
    HttpStatusCode[HttpStatusCode["NGINX_SSL_CERTIFICATE_ERROR"] = 495] = "NGINX_SSL_CERTIFICATE_ERROR";
    /** An expansion of the 400 Bad Request response code, used when a client certificate is required but not provided. */
    HttpStatusCode[HttpStatusCode["NGINX_SSL_CERTIFICATE_REQUIRED"] = 496] = "NGINX_SSL_CERTIFICATE_REQUIRED";
    /** An expansion of the 400 Bad Request response code, used when the client has made a HTTP request to a port listening
     * for HTTPS requests. */
    HttpStatusCode[HttpStatusCode["NGINX_HTTP_REQUEST_SENT_TO_HTTPS_PORT"] = 497] = "NGINX_HTTP_REQUEST_SENT_TO_HTTPS_PORT";
    /** Used when the client has closed the request before the server could send a response. */
    HttpStatusCode[HttpStatusCode["NGINX_CLIENT_CLOSED_REQUEST"] = 499] = "NGINX_CLIENT_CLOSED_REQUEST";
    // =================== BEGIN CLOUDFLARE =========================
    /** The 520 error is used as a "catch-all response for when the origin server returns something unexpected", listing connection
     * resets, large headers, and empty or invalid responses as common triggers.*/
    HttpStatusCode[HttpStatusCode["CLOUDFLARE_UNKNOWN_ERROR"] = 520] = "CLOUDFLARE_UNKNOWN_ERROR";
    /** The origin server has refused the connection from Cloudflare. */
    HttpStatusCode[HttpStatusCode["CLOUDFLARE_WEB_SERVER_IS_DOWN"] = 521] = "CLOUDFLARE_WEB_SERVER_IS_DOWN";
    /** Cloudflare could not negotiate a TCP handshake with the origin server. */
    HttpStatusCode[HttpStatusCode["CLOUDFLARE_CONNECTION_TIMED_OUT"] = 522] = "CLOUDFLARE_CONNECTION_TIMED_OUT";
    /** Cloudflare could not reach the origin server; for example, if the DNS records for the origin server are incorrect. */
    HttpStatusCode[HttpStatusCode["CLOUDFLARE_ORIGIN_IS_UNREACHABLE"] = 523] = "CLOUDFLARE_ORIGIN_IS_UNREACHABLE";
    /** Cloudflare was able to complete a TCP connection to the origin server, but did not receive a timely HTTP response. */
    HttpStatusCode[HttpStatusCode["CLOUDFLARE_A_TIMEOUT_OCCURRED"] = 524] = "CLOUDFLARE_A_TIMEOUT_OCCURRED";
    /** Cloudflare could not negotiate a SSL/TLS handshake with the origin server. */
    HttpStatusCode[HttpStatusCode["CLOUDFLARE_SSL_HANDSHAKE_FAILED"] = 525] = "CLOUDFLARE_SSL_HANDSHAKE_FAILED";
    /** Cloudflare could not validate the SSL/TLS certificate that the origin server presented. */
    HttpStatusCode[HttpStatusCode["CLOUDFLARE_INVALID_SSL_CERTIFICATE"] = 526] = "CLOUDFLARE_INVALID_SSL_CERTIFICATE";
    /** Error 527 indicates that the request timed out or failed after the WAN connection had been established.[84] */
    HttpStatusCode[HttpStatusCode["CLOUDFLARE_RAILGUN_ERROR"] = 527] = "CLOUDFLARE_RAILGUN_ERROR";
})(HttpStatusCode || (HttpStatusCode = {}));
var HttpStatus;
(function (HttpStatus) {
    // ================== BEGIN STANDARD CODES ========================
    /** The server has received the request headers and the client should proceed to send the request
     * body (in the case of a request for which a body needs to be sent; for example, a POST request).
     * Sending a large request body to a server after a request has been rejected for inappropriate headers
     * would be inefficient. To have a server check the request's headers, a client must send Expect: 100-continue
     * as a header in its initial request and receive a 100 Continue status code in response before sending the body.
     * If the client receives an error code such as 403 (Forbidden) or 405 (Method Not Allowed) then it shouldn't send
     * the request's body. The response 417 Expectation Failed indicates that the request should be repeated without
     * the Expect header as it indicates that the server doesn't support expectations (this is the case, for example, of HTTP/1.0 servers). */
    HttpStatus["CONTINUE"] = "CONTINUE";
    /** The requester has asked the server to switch protocols and the server has agreed to do so. */
    HttpStatus["SWITCHING_PROTOCOLS"] = "SWITCHING_PROTOCOLS";
    /** A WebDAV request may contain many sub-requests involving file operations, requiring a long time
     * to complete the request. This code indicates that the server has received and is processing the request,
     * but no response is available yet. This prevents the client from timing out and assuming the request was lost. */
    HttpStatus["PROCESSING"] = "PROCESSING";
    /** Used to return some response headers before file HTTP message. */
    HttpStatus["EARLY_HINTS"] = "EARLY_HINTS";
    /** Standard response for successful HTTP requests. The actual response will depend on the request method used.
     * In a GET request, the response will contain an entity corresponding to the requested resource. In a POST request,
     * the response will contain an entity describing or containing the result of the action. */
    HttpStatus["OK"] = "OK";
    /** The request has been fulfilled, resulting in the creation of a new resource. */
    HttpStatus["CREATED"] = "CREATED";
    /** The request has been accepted for processing, but the processing has not been completed. The request might or might
     * not be eventually acted upon, and may be disallowed when processing occurs. */
    HttpStatus["ACCEPTED"] = "ACCEPTED";
    /** The server is a transforming proxy (e.g. a Web accelerator) that received a 200 OK from its origin, but is returning
     * a modified version of the origin's response. */
    HttpStatus["NON_AUTHORITATIVE_INFORMATION"] = "NON_AUTHORITATIVE_INFORMATION";
    /** The server successfully processed the request and is not returning any content. */
    HttpStatus["NO_CONTENT"] = "NO_CONTENT";
    /** The server successfully processed the request, but is not returning any content. Unlike a 204 response, this response
     * requires that the requester reset the document view. */
    HttpStatus["RESET_CONTENT"] = "RESET_CONTENT";
    /** The server is delivering only part of the resource (byte serving) due to a range header sent by the client. The range
     * header is used by HTTP clients to enable resuming of interrupted downloads, or split a download into multiple simultaneous streams. */
    HttpStatus["PARTIAL_CONTENT"] = "PARTIAL_CONTENT";
    /** The message body that follows is by default an XML message and can contain a number of separate response codes, depending
     * on how many sub-requests were made. */
    HttpStatus["MULTI_STATUS"] = "MULTI_STATUS";
    /** The members of a DAV binding have already been enumerated in a preceding part of the (multistatus) response, and are not
     * being included again. */
    HttpStatus["ALREADY_REPORTED"] = "ALREADY_REPORTED";
    /** The server has fulfilled a request for the resource, and the response is a representation of the result of one or more
     * instance-manipulations applied to the current instance */
    HttpStatus["IM_USED"] = "IM_USED";
    /** Indicates multiple options for the resource from which the client may choose (via agent-driven content negotiation).
     * For example, this code could be used to present multiple video format options, to list files with different filename
     * extensions, or to suggest word-sense disambiguation. */
    HttpStatus["MULTIPLE_CHOICES"] = "MULTIPLE_CHOICES";
    /** This and all future requests should be directed to the given URI. */
    HttpStatus["MOVED_PERMANENTLY"] = "MOVED_PERMANENTLY";
    /** This is an example of industry practice contradicting the standard. The HTTP/1.0 specification (RFC 1945) required the
     * client to perform a temporary redirect (the original describing phrase was "Moved Temporarily"), but popular browsers implemented
     * 302 with the functionality of a 303 See Other. Therefore, HTTP/1.1 added status codes 303 and 307 to distinguish between the two
     * behaviours. However, some Web applications and frameworks use the 302 status code as if it were the 303. */
    HttpStatus["FOUND"] = "FOUND";
    /** The response to the request can be found under another URI using the GET method. When received in response to a POST (or PUT/DELETE),
     * the client should presume that the server has received the data and should issue a new GET request to the given URI. */
    HttpStatus["SEE_OTHER"] = "SEE_OTHER";
    /** Indicates that the resource has not been modified since the version specified by the request headers If-Modified-Since or
     * If-None-Match. In such case, there is no need to retransmit the resource since the client still has a previously-downloaded copy. */
    HttpStatus["NOT_MODIFIED"] = "NOT_MODIFIED";
    /** The requested resource is available only through a proxy, the address for which is provided in the response. Many HTTP clients
     * (such as Mozilla and Internet Explorer) do not correctly handle responses with this status code, primarily for security reasons. */
    HttpStatus["USE_PROXY"] = "USE_PROXY";
    /** No longer used. Originally meant "Subsequent requests should use the specified proxy.*/
    HttpStatus["SWITCH_PROXY"] = "SWITCH_PROXY";
    /** In this case, the request should be repeated with another URI; however, future requests should still use the original URI.
     * In contrast to how 302 was historically implemented, the request method is not allowed to be changed when reissuing the original
     * request. For example, a POST request should be repeated using another POST request. */
    HttpStatus["TEMPORARY_REDIRECT"] = "TEMPORARY_REDIRECT";
    /** The request and all future requests should be repeated using another URI. 307 and 308 parallel the behaviors of 302 and 301, but
     * do not allow the HTTP method to change. So, for example, submitting a form to a permanently redirected resource may continue
     * smoothly */
    HttpStatus["PERMANENT_REDIRECT"] = "PERMANENT_REDIRECT";
    /** The server cannot or will not process the request due to an apparent client error (e.g., malformed request syntax, size too
     * large, invalid request message framing, or deceptive request routing). */
    HttpStatus["BAD_REQUEST"] = "BAD_REQUEST";
    /** Similar to 403 Forbidden, but specifically for use when authentication is required and has failed or has not yet been provided.
   * The response must include a WWW-Authenticate header field containing a challenge applicable to the requested resource. See Basic
   * access authentication and Digest access authentication. 401 semantically means "unauthenticated", i.e. the user does not have the
   * necessary credentials.
   Note: Some sites issue HTTP 401 when an IP address is banned from the website (usually the website domain) and that specific address
   is refused permission to access a website. */
    HttpStatus["UNAUTHORIZED"] = "UNAUTHORIZED";
    /** Reserved for future use. The original intention was that this code might be used as part of some form of digital cash or micropayment
     * scheme, as proposed for example by GNU Taler, but that has not yet happened, and this code is not usually used. Google Developers API
     * uses this status if a particular developer has exceeded the daily limit on requests. Stripe API uses this code for errors with
     * processing credit cards. */
    HttpStatus["PAYMENT_REQUIRED"] = "PAYMENT_REQUIRED";
    /** The request was valid, but the server is refusing action. The user might not have the necessary permissions for a resource,
     * or may need an account of some sort. */
    HttpStatus["FORBIDDEN"] = "FORBIDDEN";
    /** The requested resource could not be found but may be available in the future. Subsequent requests by the client are permissible. */
    HttpStatus["NOT_FOUND"] = "NOT_FOUND";
    /** A request method is not supported for the requested resource; for example, a GET request on a form that requires data to be
     * presented via POST, or a PUT request on a read-only resource. */
    HttpStatus["METHOD_NOT_ALLOWED"] = "METHOD_NOT_ALLOWED";
    /** The requested resource is capable of generating only content not acceptable according to the Accept headers sent in the request. */
    HttpStatus["NOT_ACCEPTABLE"] = "NOT_ACCEPTABLE";
    /** The client must first authenticate itself with the proxy. */
    HttpStatus["PROXY_AUTHENTICATION_REQUIRED"] = "PROXY_AUTHENTICATION_REQUIRED";
    /** The server timed out waiting for the request. According to HTTP specifications: "The client did not produce a request within
     * the time that the server was prepared to wait. The client MAY repeat the request without modifications at any later time." */
    HttpStatus["REQUEST_TIMEOUT"] = "REQUEST_TIMEOUT";
    /** Indicates that the request could not be processed because of conflict in the request, such as an edit conflict between multiple
     * simultaneous updates. */
    HttpStatus["CONFLICT"] = "CONFLICT";
    /** Indicates that the resource requested is no longer available and will not be available again. This should be used when a
     * resource has been intentionally removed and the resource should be purged. Upon receiving a 410 status code, the client should
     * not request the resource in the future. Clients such as search engines should remove the resource from their indices. Most use
     * cases do not require clients and search engines to purge the resource, and a "404 Not Found" may be used instead. */
    HttpStatus["GONE"] = "GONE";
    /** The request did not specify the length of its content, which is required by the requested resource. */
    HttpStatus["LENGTH_REQUIRED"] = "LENGTH_REQUIRED";
    /** The server does not meet one of the preconditions that the requester put on the request. */
    HttpStatus["PRECONDITION_FAILED"] = "PRECONDITION_FAILED";
    /** The request is larger than the server is willing or able to process. Previously called "Request Entity Too Large". */
    HttpStatus["PAYLOAD_TOO_LARGE"] = "PAYLOAD_TOO_LARGE";
    /** The URI provided was too long for the server to process. Often the result of too much data being encoded as a query-string of
     * a GET request, in which case it should be converted to a POST request. Called "Request-URI Too Long" previously. */
    HttpStatus["URI_TOO_LONG"] = "URI_TOO_LONG";
    /** The request entity has a media type which the server or resource does not support. For example, the client uploads an image as
     * image/svg+xml, but the server requires that images use a different format. */
    HttpStatus["UNSUPPORTED_MEDIA_TYPE"] = "UNSUPPORTED_MEDIA_TYPE";
    /** The client has asked for a portion of the file (byte serving), but the server cannot supply that portion. For example, if the
     * client asked for a part of the file that lies beyond the end of the file. Called "Requested Range Not Satisfiable" previously. */
    HttpStatus["RANGE_NOT_SATISFIABLE"] = "RANGE_NOT_SATISFIABLE";
    /** The server cannot meet the requirements of the Expect request-header field. */
    HttpStatus["EXPECTATION_FAILED"] = "EXPECTATION_FAILED";
    /** This code was defined in 1998 as one of the traditional IETF April Fools' jokes, in RFC 2324, Hyper Text Coffee Pot Control
     * Protocol, and is not expected to be implemented by actual HTTP servers. The RFC specifies this code should be returned by
     * teapots requested to brew coffee. This HTTP status is used as an Easter egg in some websites, including Google.com. */
    HttpStatus["IM_A_TEAPOT"] = "IM_A_TEAPOT";
    /** The request was directed at a server that is not able to produce a response. (for example because of a connection reuse) */
    HttpStatus["MISDIRECT_REQUEST"] = "MISDIRECT_REQUEST";
    /** The request was well-formed but was unable to be followed due to semantic errors. */
    HttpStatus["UNPROCESSABLE_ENTITY"] = "UNPROCESSABLE_ENTITY";
    /** The resource that is being accessed is locked. */
    HttpStatus["LOCKED"] = "LOCKED";
    /** The request failed because it depended on another request and that request failed (e.g., a PROPPATCH). */
    HttpStatus["FAILED_DEPENDENCY"] = "FAILED_DEPENDENCY";
    /** The client should switch to a different protocol such as TLS/1.0, given in the Upgrade header field. */
    HttpStatus["UPGRADE_REQUIRED"] = "UPGRADE_REQUIRED";
    /** The origin server requires the request to be conditional. Intended to prevent the 'lost update' problem, where a client
     * GETs a resource's state, modifies it, and PUTs it back to the server, when meanwhile a third party has modified the state
     * on the server, leading to a conflict." */
    HttpStatus["PRECONDITION_REQUIRED"] = "PRECONDITION_REQUIRED";
    /** The user has sent too many requests in a given amount of time. Intended for use with rate-limiting schemes. */
    HttpStatus["TOO_MANY_REQUESTS"] = "TOO_MANY_REQUESTS";
    /** The server is unwilling to process the request because either an individual header field, or all the header fields
     * collectively, are too large. */
    HttpStatus["REQUEST_HEADER_FIELDS_TOO_LARGE"] = "REQUEST_HEADER_FIELDS_TOO_LARGE";
    /** A server operator has received a legal demand to deny access to a resource or to a set of resources that includes the
     * requested resource. The code 451 was chosen as a reference to the novel Fahrenheit 451 (see the Acknowledgements in the RFC). */
    HttpStatus["UNAVAILABLE_FOR_LEGAL_REASONS"] = "UNAVAILABLE_FOR_LEGAL_REASONS";
    /** A generic error message, given when an unexpected condition was encountered and no more specific message is suitable. */
    HttpStatus["INTERNAL_SERVER_ERROR"] = "INTERNAL_SERVER_ERROR";
    /** The server either does not recognize the request method, or it lacks the ability to fulfil the request. Usually this
     * implies future availability (e.g., a new feature of a web-service API). */
    HttpStatus["NOT_IMPLEMENTED"] = "NOT_IMPLEMENTED";
    /** The server was acting as a gateway or proxy and received an invalid response from the upstream server. */
    HttpStatus["BAD_GATEWAY"] = "BAD_GATEWAY";
    /** The server is currently unavailable (because it is overloaded or down for maintenance). Generally, this is a temporary state. */
    HttpStatus["SERVICE_UNAVAILABLE"] = "SERVICE_UNAVAILABLE";
    /** The server was acting as a gateway or proxy and did not receive a timely response from the upstream server. */
    HttpStatus["GATEWAY_TIMEOUT"] = "GATEWAY_TIMEOUT";
    /** The server does not support the HTTP protocol version used in the request. */
    HttpStatus["HTTP_VERSION_NOT_SUPPORTED"] = "HTTP_VERSION_NOT_SUPPORTED";
    /** Transparent content negotiation for the request results in a circular reference. */
    HttpStatus["VARIANT_ALSO_NEGOTIATES"] = "VARIANT_ALSO_NEGOTIATES";
    /** The server is unable to store the representation needed to complete the request. */
    HttpStatus["INSUFFICIENT_STORAGE"] = "INSUFFICIENT_STORAGE";
    /** The server detected an infinite loop while processing the request (sent in lieu of 208 Already Reported). */
    HttpStatus["LOOP_DETECTED"] = "LOOP_DETECTED";
    /** Further extensions to the request are required for the server to fulfil it. */
    HttpStatus["NOT_EXTENDED"] = "NOT_EXTENDED";
    /** The client needs to authenticate to gain network access. Intended for use by intercepting proxies used to control access to
     * the network (e.g., "captive portals" used to require agreement to Terms of Service before granting full Internet access via
     * a Wi-Fi hotspot). */
    HttpStatus["NETWORK_AUTHENTICATION_REQUIRED"] = "NETWORK_AUTHENTICATION_REQUIRED";
    // ================== BEGIN NON-STANDARD CODES ========================
    /** Used in the resumable requests proposal to resume aborted PUT or POST requests.[69] */
    HttpStatus["CHECKPOINT"] = "CHECKPOINT";
    /** A deprecated response used by the Spring Framework when a method has failed.[70] */
    HttpStatus["METHOD_FAILURE_SPRING_FRAMEWORK"] = "METHOD_FAILURE_SPRING_FRAMEWORK";
    /** Returned by version 1 of the Twitter Search and Trends API when the client is being rate limited; versions 1.1 and later use
     * the 429 Too Many Requests response code instead.[71] */
    HttpStatus["ENHANCE_YOUR_CALM"] = "ENHANCE_YOUR_CALM";
    /** The Microsoft extension code indicated when Windows Parental Controls are turned on and are blocking access to the requested
     * webpage.[72] */
    HttpStatus["BLOCKED_BY_WINDOWS_PARENTAL_CONTROLS"] = "BLOCKED_BY_WINDOWS_PARENTAL_CONTROLS";
    /** Returned by ArcGIS for Server. Code 498 indicates an expired or otherwise invalid token.[73] */
    HttpStatus["INVALID_TOKEN_ESRI"] = "INVALID_TOKEN_ESRI";
    /** Returned by ArcGIS for Server. Code 499 indicates that a token is required but was not submitted.[73] */
    HttpStatus["TOKEN_REQUIRED_ESRI"] = "TOKEN_REQUIRED_ESRI";
    /** Used by GoCardless to indicate that a given token or secret is invalid */
    HttpStatus["INVALID_GO_CARDLESS_TOKEN"] = "INVALID_GO_CARDLESS_TOKEN";
    /** The server has exceeded the bandwidth specified by the server administrator; this is often used by shared hosting providers
     * to limit the bandwidth of customers.[74] */
    HttpStatus["BANDWIDTH_LIMIT_EXCEEDED"] = "BANDWIDTH_LIMIT_EXCEEDED";
    /** Used by the Pantheon web platform to indicate a site that has been frozen due to inactivity.[75] */
    HttpStatus["SITE_IS_FROZEN"] = "SITE_IS_FROZEN";
    /** Used by some HTTP proxies to signal a network read timeout behind the proxy to a client in front of the proxy.[76] */
    HttpStatus["NETWORK_READ_TIMEOUT_ERROR"] = "NETWORK_READ_TIMEOUT_ERROR";
    // =================== BEGIN MICROSOFT IIS =========================
    /** The client's session has expired and must log in again.[77] */
    HttpStatus["IIS_LOGIN_TIMEOUT"] = "IIS_LOGIN_TIMEOUT";
    /** The server cannot honour the request because the user has not provided the required information.[78] */
    HttpStatus["IIS_RETRY_WITH"] = "IIS_RETRY_WITH";
    /** Used in Exchange ActiveSync when either a more efficient server is available or the server cannot access the users'
     * mailbox.[79] The client is expected to re-run the HTTP AutoDiscover operation to find a more appropriate server.[80] */
    HttpStatus["IIS_REDIRECT"] = "IIS_REDIRECT";
    // ==================== BEGIN NGINX =================================
    /** Used to indicate that the server has returned no information to the client and closed the connection. */
    HttpStatus["NGINX_NO_RESPONSE"] = "NGINX_NO_RESPONSE";
    /** An expansion of the 400 Bad Request response code, used when the client has provided an invalid client certificate. */
    HttpStatus["NGINX_SSL_CERTIFICATE_ERROR"] = "NGINX_SSL_CERTIFICATE_ERROR";
    /** An expansion of the 400 Bad Request response code, used when a client certificate is required but not provided. */
    HttpStatus["NGINX_SSL_CERTIFICATE_REQUIRED"] = "NGINX_SSL_CERTIFICATE_REQUIRED";
    /** An expansion of the 400 Bad Request response code, used when the client has made a HTTP request to a port listening
     * for HTTPS requests. */
    HttpStatus["NGINX_HTTP_REQUEST_SENT_TO_HTTPS_PORT"] = "NGINX_HTTP_REQUEST_SENT_TO_HTTPS_PORT";
    /** Used when the client has closed the request before the server could send a response. */
    HttpStatus["NGINX_CLIENT_CLOSED_REQUEST"] = "NGINX_CLIENT_CLOSED_REQUEST";
    // =================== BEGIN CLOUDFLARE =========================
    /** The 520 error is used as a "catch-all response for when the origin server returns something unexpected", listing connection
     * resets, large headers, and empty or invalid responses as common triggers.*/
    HttpStatus["CLOUDFLARE_UNKNOWN_ERROR"] = "CLOUDFLARE_UNKNOWN_ERROR";
    /** The origin server has refused the connection from Cloudflare. */
    HttpStatus["CLOUDFLARE_WEB_SERVER_IS_DOWN"] = "CLOUDFLARE_WEB_SERVER_IS_DOWN";
    /** Cloudflare could not negotiate a TCP handshake with the origin server. */
    HttpStatus["CLOUDFLARE_CONNECTION_TIMED_OUT"] = "CLOUDFLARE_CONNECTION_TIMED_OUT";
    /** Cloudflare could not reach the origin server; for example, if the DNS records for the origin server are incorrect. */
    HttpStatus["CLOUDFLARE_ORIGIN_IS_UNREACHABLE"] = "CLOUDFLARE_ORIGIN_IS_UNREACHABLE";
    /** Cloudflare was able to complete a TCP connection to the origin server, but did not receive a timely HTTP response. */
    HttpStatus["CLOUDFLARE_A_TIMEOUT_OCCURRED"] = "CLOUDFLARE_A_TIMEOUT_OCCURRED";
    /** Cloudflare could not negotiate a SSL/TLS handshake with the origin server. */
    HttpStatus["CLOUDFLARE_SSL_HANDSHAKE_FAILED"] = "CLOUDFLARE_SSL_HANDSHAKE_FAILED";
    /** Cloudflare could not validate the SSL/TLS certificate that the origin server presented. */
    HttpStatus["CLOUDFLARE_INVALID_SSL_CERTIFICATE"] = "CLOUDFLARE_INVALID_SSL_CERTIFICATE";
    /** Error 527 indicates that the request timed out or failed after the WAN connection had been established.[84] */
    HttpStatus["CLOUDFLARE_RAILGUN_ERROR"] = "CLOUDFLARE_RAILGUN_ERROR";
})(HttpStatus || (HttpStatus = {}));

function ApiContract(options) {
    return (target) => {
        Reflect.defineMetadata('apiContract', options, target);
    };
}

/*
 * Public API Surface of http
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ApiContract, BaseApiErrorCodes, HttpMethods, HttpModule, HttpService, HttpStatus, HttpStatusCode };

